// Authentication / authorisation interface.
// IMPORTANT: everything here is a UI hint. Hiding a control is NOT security.
// The UGAIN API enforces every permission server-side (server/src/routes.ts).
import type { Activity, DemoData, Task } from '../domain/types';
import { isDemo } from '../lib/clock';

export type Role = 'ambassador' | 'organiser' | 'moderator';

// No token lives in the browser: the server session is an HttpOnly cookie
// (see services/session.ts). This is only the UI's view of who is signed in.
export interface Session {
  ambassadorId: string;
  roles: readonly Role[];
  /** Server roles from the bootstrap: 'admin' | 'me_approver'. Empty for ordinary members. */
  serverRoles: readonly string[];
  authMode: 'demo' | 'google' | 'local-dev';
}

const demoSession: Session = { ambassadorId: 'SR', roles: ['ambassador', 'organiser'], serverRoles: [], authMode: 'demo' };
let current: Session = isDemo ? demoSession : { ambassadorId: '', roles: [], serverRoles: [], authMode: 'google' };

/** Demo: fixed identity. Api mode: set from GET /api/me/bootstrap (store.tsx). */
export function getSession(): Session {
  return current;
}

/** Api mode: adopt the identity the server returned. */
export function sessionFromBootstrap(d: DemoData): void {
  const v = d.viewer;
  current = {
    ambassadorId: d.me,
    roles: ['ambassador', ...((v?.leadOf.length ?? 0) > 0 ? (['organiser'] as const) : []), ...((v?.roles.includes('me_approver') || v?.roles.includes('admin')) ? (['moderator'] as const) : [])],
    serverRoles: v?.roles ?? [],
    authMode: v?.authMode ?? 'google',
  };
}

export const canTickTask = (s: Session, t: Task): boolean => t.ownerId === s.ambassadorId;
export const canUpdateActivity = (s: Session, a: Activity): boolean => a.team.some((m) => m.ambassadorId === s.ambassadorId);
export const canAcceptOffers = (s: Session, a: Activity): boolean => a.organiserId === s.ambassadorId;
export const isApprover = (s: Session): boolean => s.serverRoles.includes('admin') || s.serverRoles.includes('me_approver');
/** Approvers review proposals, but never their own. */
export const canReview = (s: Session, a: Activity): boolean => isApprover(s) && a.review === 'pending' && a.proposedBy !== s.ambassadorId;
export const canAddTasks = (s: Session, a: Activity): boolean => a.stage > 0 && (a.organiserId === s.ambassadorId || isApprover(s));
